import { useEffect, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Head from 'components/Header/Head';
import Header from 'components/Header/Header';
import ArticleNav from 'components/Post/Article/ArticleNav';
import ArticleBody from 'components/Post/Article/ArticleBody';
import RouteFlourish from 'components/Post/Article/RouteFlourish';
import Footer from 'components/General/Footer';
import { getUser } from 'helpers/token';
import Image from 'components/FutureImage';
import { fraunces } from 'components/Landing/fonts';
import {
  ArrowIcon,
  CtaButton,
  Eyebrow,
  GhostButton,
  INK,
  MUTED,
  PAPER_CARD,
} from 'components/Landing/ui';
import cover from '../../public/img/polarsteps/cover.jpg';
import polarstepsShot from '../../public/img/blog-platforms/polarsteps.jpg';
import articleShot from '../../public/img/blog-platforms/travelfeed-article.jpg';

// Signup CTAs open the boarding-pass JoinDialog (via LoginButton), never
// the legacy /join page; lazy so the dialog stack stays out of the bundle.
const LoginButton = dynamic(
  () => import('components/Header/LoginButton'),
  { ssr: false },
);

const CANONICAL = 'https://travelfeed.com/polarsteps-alternative';
const COVER_URL = 'https://travelfeed.com/img/polarsteps/cover.jpg';

// Rendered as visible FAQ AND FAQPage JSON-LD — keep both from one array.
const FAQ = [
  {
    questionName: 'Is Polarsteps free?',
    acceptedAnswerText:
      'The core app is free and ad-free: route tracking, trip steps and sharing cost nothing. Polarsteps earns through printed Travel Books (priced at checkout by page count and binding) and the Polarsteps Plus subscription (€8.99/month or €29.99/year, launched July 2026) which adds 3D maps, trip replays and more map styles. Nothing about the free tier forces you to pay.',
  },
  {
    questionName: 'Can Polarsteps make a real website for my travels?',
    acceptedAnswerText:
      "No. Public trips get a shareable page on polarsteps.com and an embeddable map, but there's no way to use your own domain, no blog-style archive for readers who don't know you, and only trips explicitly set to public are even indexable by Google. If you want your travels at your own address, you need a travel blog — which can happily coexist with Polarsteps tracking.",
  },
  {
    questionName: 'Can I get my content out of Polarsteps?',
    acceptedAnswerText:
      'Yes, with limits. The account archive export gives you your photos as image files plus your trips, steps and coordinates as JSON. There is no native GPX export and no automatic way to turn the archive into a website — republishing your trips as blog posts is a manual job. Export before deleting anything: deleting your account permanently removes trips, photos and text.',
  },
  {
    questionName: 'What is the best Polarsteps alternative?',
    acceptedAnswerText:
      "Depends on what you actually want. For automatic route tracking, Polarsteps is honestly hard to beat — most 'alternatives' are smaller apps with the same limitation: your travels live inside an app. If the thing you're missing is writing, readers and a site of your own, the alternative isn't another tracker, it's a travel blog. TravelFeed is built for exactly that switch: real long-form posts, your own domain, maps, and a travel readership from day one.",
  },
];

function CompareRow({ label, polarsteps, travelfeed }) {
  return (
    <tr>
      <td className="tw-font-semibold">{label}</td>
      <td>{polarsteps}</td>
      <td>{travelfeed}</td>
    </tr>
  );
}

// Head-to-head lander: most winnable SERP of the blogger-intent cluster
// (docs/seo-plan.md §3). Honest by design — praises Polarsteps at what it
// does well, converts the writers.
export default function PolarstepsAlternative() {
  const [isMember, setIsMember] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    if (getUser()) setIsMember(true);
  }, []);

  useEffect(() => {
    document.body.classList.add('tf-paper');
    return () => document.body.classList.remove('tf-paper');
  }, []);

  return (
    <>
      <Head
        shorttitle="Polarsteps Alternative: A Travel Journal That's a Real Website"
        description="Polarsteps is a great trip tracker — but your travels live inside an app. The honest comparison with a real travel blog: your own domain, long-form stories, Google, and what each does better."
        seo={{ canonicalUrl: CANONICAL }}
        image={`https://og.travelfeed.com/api/post?title=${encodeURIComponent(
          "Polarsteps Alternative: A Travel Journal That's a Real Website",
        )}&image=${encodeURIComponent(COVER_URL)}`}
        post={{
          author: { username: 'jpphotography', displayName: 'Julian' },
          createdAt: '2026-07-16',
          updatedAt: '2026-07-16',
          tags: ['travel-blogging'],
          thumbnail: COVER_URL,
        }}
        faq={FAQ}
      />
      {isMember ? <Header /> : <ArticleNav />}
      <div className="tw-pt-20" />

      <header className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-4 tw-pt-8 sm:tw-pt-12">
        <Eyebrow>Honest head-to-head</Eyebrow>
        <h1
          className={`${fraunces.className} tw-m-0 tw-text-4xl tw-font-semibold tw-leading-[1.08] tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-5xl`}
        >
          The Polarsteps alternative for travelers who write
        </h1>
        <p className={`${MUTED} tw-mb-0 tw-mt-5 tw-text-lg tw-leading-relaxed`}>
          Polarsteps is a lovely trip tracker — we&rsquo;ll say so repeatedly
          below. But a dotted line on an app&rsquo;s map isn&rsquo;t a travel
          journal that&rsquo;s a real website. Here&rsquo;s the honest
          comparison, including what Polarsteps does better.
        </p>
        <p className="tw-mb-0 tw-mt-5 tw-font-mono tw-text-[11px] tw-uppercase tw-tracking-[0.18em] tw-text-[#8A958F]">
          By{' '}
          <Link
            href="/@jpphotography"
            className="tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline"
          >
            Julian
          </Link>
          , founder of TravelFeed · Updated July 2026
        </p>
      </header>

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pb-6 tw-pt-4">
        <div className="tw-rounded-[4px] tw-bg-white dark:tw-bg-[#EFEAE0] tw-p-3 tw-shadow-[0_30px_60px_-20px_rgba(29,45,40,0.35)]">
          <Image
            src={cover}
            alt="Painted flat-lay: a phone showing a dotted route map beside a postcard-sized browser window with a travel blog inside"
            priority
            sizes="(max-width: 1024px) 92vw, 990px"
            className="tw-h-auto tw-w-full tw-rounded-[2px]"
          />
        </div>
      </div>

      <ArticleBody>
        <p>
          Search for a Polarsteps alternative and you&rsquo;ll find a page of
          smaller apps promising the same thing with a different logo. This
          page argues something else: if you&rsquo;re unsatisfied with
          Polarsteps, the odds are the problem isn&rsquo;t the tracker —
          it&rsquo;s that a tracker is all you have. The people who outgrow
          Polarsteps are almost always the ones who started
          <em> writing</em>.
        </p>

        <h2 id="what-polarsteps-gets-right">What Polarsteps gets right</h2>
        <p>
          Credit first, and honestly earned. Polarsteps records your route
          automatically — phone in pocket, dotted line appears, even offline.
          The maps are beautiful, the printed Travel Books are a genuinely
          lovely keepsake, the core app is free without ads, and the new Plus
          subscription (€8.99/month or €29.99/year) stays optional. For
          letting family follow the dotted line while you travel,
          it&rsquo;s excellent, and nothing below changes that.
        </p>
        <p>
          If that&rsquo;s all you want, keep Polarsteps and close this tab
          with our blessing.
        </p>

        <h2 id="where-an-app-stops-being-enough">
          Where an app stops being enough
        </h2>
        <p>
          The limits show up when the trip matters more in hindsight — when
          you want the stories to be <em>findable</em>, <em>readable</em> and{' '}
          <em>yours</em>:
        </p>
        <ul>
          <li>
            <strong>No address of your own.</strong> Your travels live at
            polarsteps.com, on the app&rsquo;s terms. There&rsquo;s no way to
            put your journal on your own domain — the one thing that makes a
            body of work portable and permanent.
          </li>
          <li>
            <strong>Practically invisible on Google.</strong> Only trips
            explicitly set to public are even indexable, and step captions
            aren&rsquo;t written — or structured — to be found by anyone who
            doesn&rsquo;t already follow you.
          </li>
          <li>
            <strong>Steps aren&rsquo;t stories.</strong> Location pins with
            captions are a log. The night train you almost missed deserves
            eight hundred words and twelve photos in order — long-form,
            structured writing is what blogs are for.
          </li>
          <li>
            <strong>The exit is a ZIP file.</strong> The export gives you
            photos plus trip data as JSON — no website, no GPX, no archive a
            reader could visit. Delete the account and everything in it is
            gone.
          </li>
          <li>
            <strong>You can never earn.</strong> There&rsquo;s no
            monetization of any kind, and the terms treat use as personal and
            non-commercial. A blog can pay for the trip; a tracker never
            will.
          </li>
        </ul>

        <h2 id="side-by-side">Side by side</h2>
        <table>
          <thead>
            <tr>
              <th> </th>
              <th>Polarsteps</th>
              <th>TravelFeed</th>
            </tr>
          </thead>
          <tbody>
            <CompareRow
              label="Automatic route tracking"
              polarsteps="Yes — its superpower"
              travelfeed="No — you write when you choose"
            />
            <CompareRow
              label="Your own domain"
              polarsteps="No"
              travelfeed="Yes, on every blog plan"
            />
            <CompareRow
              label="Long-form stories"
              polarsteps="Captions on steps"
              travelfeed="Full posts with galleries, maps and formatting"
            />
            <CompareRow
              label="A page per destination"
              polarsteps="Trips on a timeline, not places"
              travelfeed="Country, region and city pages, built from your posts"
            />
            <CompareRow
              label="Found on Google"
              polarsteps="Only public trips, rarely surfaced"
              travelfeed="Built for it — fast pages, clean SEO"
            />
            <CompareRow
              label="Readers you don't know yet"
              polarsteps="Followers you invite"
              travelfeed="A travel community from day one"
            />
            <CompareRow
              label="Earning"
              polarsteps="None — personal use only"
              travelfeed="Rewards from your first post; your affiliate income stays yours"
            />
            <CompareRow
              label="Printed keepsake"
              polarsteps="Travel Books, priced by size"
              travelfeed="No — we make websites, not books"
            />
            <CompareRow
              label="Cost"
              polarsteps="Free · Plus €29.99/yr"
              travelfeed="$19/yr community · blogs from $15/mo"
            />
          </tbody>
        </table>
        <p>
          Notice the two rows we lose. If automatic tracking or a printed
          book is the point, Polarsteps is the right product — this
          comparison only turns in our favor when the writing is.
        </p>

        <h2 id="the-difference-in-practice">The difference, in practice</h2>
        <p>Polarsteps, as it presents itself — an app, tracking trips:</p>
        <figure className="tf-wide">
          <Image
            src={polarstepsShot}
            alt="Polarsteps homepage — one travel app for all your adventures"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
        </figure>
        <p>
          A travel story on a TravelFeed blog — a real page at a real
          address, written for someone who wasn&rsquo;t there:
        </p>
        <figure className="tf-wide">
          <Image
            src={articleShot}
            alt="A live TravelFeed article with a full-width photo masthead, GPS coordinates and editorial typography"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
        </figure>

        <h2 id="keep-both">The honest recommendation: keep both</h2>
        <p>
          This isn&rsquo;t either/or. Plenty of TravelFeed members track with
          Polarsteps while they move and write the real stories on their blog
          when the dust settles — the tracker remembers the route, the blog
          remembers why it mattered. What we&rsquo;d gently argue against is
          the app being the <em>only</em> home your travels have: apps
          retire, accounts get deleted, and a dotted line without the
          stories fades fast. If you&rsquo;re not sure a blog is for you,
          start with{' '}
          <Link href="/start-a-travel-blog">
            our honest guide to starting a travel blog
          </Link>{' '}
          or see{' '}
          <Link href="/blog/best-travel-blog-platforms">
            how every platform compares
          </Link>
          .
        </p>

        <h2 id="faq">Frequently asked questions</h2>
        {FAQ.map(entry => (
          <div key={entry.questionName}>
            <h3>{entry.questionName}</h3>
            <p>{entry.acceptedAnswerText}</p>
          </div>
        ))}
      </ArticleBody>

      <section className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-16 tw-pt-8">
        <RouteFlourish className="tw-pb-10" />
        <div
          className={`${PAPER_CARD} tw-rounded-2xl tw-p-7 tw-text-center sm:tw-p-10`}
        >
          <p
            className={`${fraunces.className} ${INK} tw-mb-3 tw-mt-0 tw-text-2xl tw-font-semibold tw-tracking-tight sm:tw-text-3xl`}
          >
            Give the stories a home of their own.
          </p>
          <p className={`${MUTED} tw-mx-auto tw-mb-7 tw-mt-0 tw-max-w-lg`}>
            Your own travel blog on your own domain — maps included, readers
            from day one, live in minutes. Keep tracking with Polarsteps;
            write where it lasts.
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
            <CtaButton onClick={() => setJoinOpen(true)}>
              Start your travel blog
              <ArrowIcon />
            </CtaButton>
            <GhostButton href="/start-a-travel-blog">
              Read the honest guide first
            </GhostButton>
          </div>
        </div>
      </section>

      {joinOpen && (
        <LoginButton
          open
          hideButtons
          onClickClose={() => setJoinOpen(false)}
        />
      )}
      <Footer isNoSpace />
    </>
  );
}
